import { IconName } from '@wui/common-library'
import {
  HistoryMessageItem,
  MessageCallAppointmentEnd,
  MessageCallAppointmentStart,
  MessageCallAnswered,
  MessageCallEnded,
  MessageCallBusy,
  MessageCallTerminated,
  CallPastMessage,
  MessageCallFailed,
  MessageCallForwarded,
  MessageCallReceived,
  MessageCallRecordCompleted,
} from '@wui/im'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getDayJSInTimezone } from '@/shared/composables/timezone'
import { TIME_FORMAT } from '@/shared/utils/constants'
import { conversionFromNumberToTime, getMonthName } from '@/shared/utils/date-time'

import { CallStatistic } from './types'

type CallEvents =
  MessageCallAppointmentStart |
  MessageCallAppointmentEnd |
  MessageCallAnswered |
  MessageCallEnded |
  MessageCallBusy |
  MessageCallTerminated |
  CallPastMessage |
  MessageCallFailed |
  MessageCallForwarded

export const getCallEventText = (message: HistoryMessageItem<CallEvents>, contactName?: string): string => {
  const { t } = useLocalization()
  const { currentUser } = useCurrentUser()

  if (message.item.event === 'appointment_start') {
    return t('common.callStarted')
  }

  if (message.item.event === 'appointment_end') {
    return t('common.callEnded')
  }

  if (message.item.event === 'call_answered') {
    return t('common.callStarted')
  }

  if (message.item.event === 'call_missed') {
    const isOutgoing = message.item.dstPServed === currentUser.value?.imLogin
    if (isOutgoing) {
      return t('chat.youMissedCall')
    }

    return t('chat.smbdMissedCall', { name: message.item.dstName ?? t('common.subscriber') })
  }

  if (message.item.event === 'call_ended') {
    return t('common.callEnded')
  }

  if (message.item.event === 'dialing_busy') {
    const isOutgoing = message.item.dstPServed === currentUser.value?.imLogin
    if (isOutgoing) {
      return t('common.incomingDialingBusy')
    }

    return t('common.subscriberIsBusy', { name: message.item.dstName ?? t('common.subscriber') })
  }

  if (message.item.event === 'dialing_terminated') {
    const isOutgoing = message.item.srcPServed === currentUser.value?.imLogin
    if (isOutgoing) {
      return t('common.outgoingDialingTerminated')
    }

    return t('common.incomingDialingTerminated')
  }

  if (message.item.event === 'call_forwarded') {
    const from = message.item.srcName || message.item.srcNumber || ''
    const to = message.item.callForwardDestination || ''
    const reason = t('common.callForwardReason.' + message.item.callForwardReason)
    if (message.item.srcPServed === currentUser.value?.imLogin) {
      return t('common.callForwardedByOther', { forwardedBy: contactName ?? message.item.callForwarder, to, reason })
    }
    return t('common.callForwardedByYou', { from, to, reason })

  }

  if (message.item.event === 'create_conference_from_dialog_failed') {
    return t('chat.failedToCreatConferenceFromDialog')
  }

  return t('common.callWasMade')
}

export const getEndedCallEventDuration = (message: HistoryMessageItem<CallEvents>): string => {
  let duration = ''
  if (message.item.event === 'appointment_end') {
    if (message.item.conferenceStatistic) {
      const { timestampStart, timestampStop } = message.item.conferenceStatistic
      if (!Number.isFinite(timestampStart) || !Number.isFinite(timestampStop) || timestampStart >= timestampStop) {
        return ''
      }

      duration = ` (${conversionFromNumberToTime((timestampStop - timestampStart) * 1000).trim()})`
    }

    return duration
  }
  return duration
}

export const getAppointmentDurationText = (
  message: HistoryMessageItem<MessageCallAppointmentEnd | MessageCallAppointmentStart>,
): string => {
  if (message.item.event !== 'appointment_end' || !message.item.conferenceStatistic) {
    return ''
  }

  const { timestampStart, timestampStop } = message.item.conferenceStatistic
  if (!Number.isFinite(timestampStart) || !Number.isFinite(timestampStop) || timestampStart >= timestampStop) {
    return ''
  }

  return conversionFromNumberToTime((timestampStop - timestampStart) * 1000).trim()
}

export const getAppointmentTimeRangeText = (
  message: HistoryMessageItem<MessageCallAppointmentEnd | MessageCallAppointmentStart>,
): string => {
  if (message.item.event === 'appointment_end' && message.item.conferenceStatistic) {
    const { timestampStart, timestampStop } = message.item.conferenceStatistic
    if (Number.isFinite(timestampStart) && Number.isFinite(timestampStop) && timestampStart < timestampStop) {
      const start = getDayJSInTimezone(timestampStart).format(TIME_FORMAT)
      const end = getDayJSInTimezone(timestampStop).format(TIME_FORMAT)
      return `${start} – ${end}`
    }
  }

  return getDayJSInTimezone(message.timestamp ?? 0).format(TIME_FORMAT)
}

export const getAppointmentDateTimeText = (
  message: HistoryMessageItem<MessageCallAppointmentEnd | MessageCallAppointmentStart>,
): string => {
  const { t } = useLocalization()

  if (message.item.event === 'appointment_end' && message.item.conferenceStatistic) {
    const { timestampStart, timestampStop } = message.item.conferenceStatistic
    if (Number.isFinite(timestampStart) && Number.isFinite(timestampStop) && timestampStart < timestampStop) {
      const start = getDayJSInTimezone(timestampStart)
      const end = getDayJSInTimezone(timestampStop)
      const month = (getMonthName(start.format('M'), 1) ?? '').toLowerCase()
      return t('chat.appointmentDateTimeRange', {
        day: start.format('D'),
        month,
        startTime: start.format(TIME_FORMAT),
        endTime: end.format(TIME_FORMAT),
      })
    }
  }

  const date = getDayJSInTimezone(message.timestamp ?? 0)
  const month = (getMonthName(date.format('M'), 1) ?? '').toLowerCase()
  return t('chat.appointmentDateTime', {
    day: date.format('D'),
    month,
    time: date.format(TIME_FORMAT),
  })
}

export const getCallRecordEvents = (
  events: HistoryMessageItem[],
  confId?: string,
): HistoryMessageItem<MessageCallRecordCompleted>[] => {
  return events.filter(e => {
    const item = e.item as MessageCallReceived
    return item?.event === 'call_record_completed'
      && (e.item as MessageCallRecordCompleted).confId === confId
  }) as HistoryMessageItem<MessageCallRecordCompleted>[]
}

export const getCallStatisticStatusText = (data: CallStatistic): string => {
  const { t } = useLocalization()

  if (data.isCallAnswered) {
    return t('chat.connectsToCall').toLowerCase()
  }
  if (data.isCallMissed) {
    return t('chat.missCall').toLowerCase()
  }
  if (data.isCallEnded) {
    return t('chat.leaveCall').toLowerCase()
  }
  if (data.isCallRejected) {
    return t('chat.rejectsCall').toLowerCase()
  }
  return ''
}

export const getCallStatisticStatusIcon = (data: CallStatistic): IconName | undefined => {
  if (data.isCallAnswered) {
    return 'arrowRightM'
  }
  if (data.isCallEnded) {
    return 'arrowLeftM'
  }
  if (data.isCallRejected) {
    return 'arrowElbowLeftM'
  }
  if (data.isCallMissed) {
    return 'arrowElbowLeftM'
  }
  return undefined
}

export const getCallStatisticActionText = (data: CallStatistic): string => {
  const { t } = useLocalization()
  const statusText = getCallStatisticStatusText(data)
  const isInviterDifferent = Boolean(
    data.inviterName && data.participantName && data.inviterName !== data.participantName,
  )

  const invitationSuffix = isInviterDifferent && !data.isCallEnded
    ? `, ${t('chat.invitationFrom', { name: data.inviterName })}`
    : ''

  if (!statusText) {
    return isInviterDifferent
      ? t('chat.invitationFrom', { name: data.inviterName })
      : ''
  }

  return `${statusText}${invitationSuffix}`
}

export const matchesCallStatisticSearch = (data: CallStatistic, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return true
  }

  const name = (data.participantName ?? '').toLowerCase()
  const inviter = (data.inviterName ?? '').toLowerCase()
  const action = getCallStatisticActionText(data).toLowerCase()

  return name.includes(normalizedQuery)
    || inviter.includes(normalizedQuery)
    || action.includes(normalizedQuery)
}
